import { FunctionComponent, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, ButtonType } from '../Button';
import { Modal } from '../Modal';
import { ShareUrlViaSocial } from '../ShareUrlViaSocial';

import classes from './ShareUrlModal.module.scss';

interface ShareUrlModalProps {
  isOpen: boolean;
  url: string;
  onClose: () => void;
}

export const ShareUrlModal: FunctionComponent<ShareUrlModalProps> = memo(
  ({ isOpen, url, onClose }) => {
    const { t } = useTranslation();

    const copyClick = useCallback(async () => {
      onClose();

      if (!navigator.clipboard || !url) {
        return;
      }

      await navigator.clipboard.writeText(url);

      toast(t('shareUrl.link-copied'));
    }, [onClose, t, url]);

    const title = t('shareUrl.title');

    const body = useMemo(
      () => (
        <div className={classes['share-url']}>
          <div className={classes['share-url__title']}>{title}</div>

          <ShareUrlViaSocial
            url={url}
            subject={t('shareUrl.email-subject')}
            body={`${t('shareUrl.email-body')}\n`}
          />

          <Button
            type={ButtonType.secondary}
            label={t('shareUrl.copy-link')}
            onClick={copyClick}
            fullWidth
          />
        </div>
      ),
      [copyClick, t, title, url]
    );

    return (
      <Modal
        isOpen={isOpen}
        body={body}
        onClose={onClose}
        contentStyle={{ width: '26rem' }}
        ariaLabel={title}
      />
    );
  }
);
